import { HiOutlineChevronRight, HiInformationCircle } from "react-icons/hi";
import { useSelector } from "react-redux";
import { Alert } from "flowbite-react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import CustomDropdown from "../../../components/common/CustomDropdown";
import useOrganizationService from "../../../services/organization";
import useJiraService from "../../../services/jira";
import useAzureService from "../../../services/azure";
import CacheResetAlert from "../../CacheResetAlert/CacheResetAlert";
import ManageProjectsModal from "../../../components/Modals/ImportIntegration/ManageProjectsModal";
import "./PriorityMappingSelector.css";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import Portal from "../../../components/Portal";

export type ExternalMapData = Record<
  string,
  {
    id: string;
    label: string;
    mapped_with: string;
  }
>;

interface PriorityMappingSelectorProps {
  integrationType: "azure" | "jira";
  onMappingChange?: (mapping: ExternalMapData) => void;
  onHasChangesChange?: (hasChanges: boolean) => void;
  onIsSavingChange?: (isSaving: boolean) => void;
  showInternalUpdate?: boolean;
}

export interface PriorityMappingSelectorRef {
  update: () => Promise<void>;
}

const PRIORITY_COLORS = [
  "#ef4444", // Red-500
  "#f97316", // Orange-500
  "#f59e0b", // Amber-500
  "#eab308", // Yellow-500
  "#84cc16", // Lime-500
  "#22c55e", // Green-500
];

const PriorityMappingSelector = forwardRef<
  PriorityMappingSelectorRef,
  PriorityMappingSelectorProps
>(
  (
    {
      integrationType,
      onMappingChange,
      onHasChangesChange,
      onIsSavingChange,
      showInternalUpdate = true,
    },
    ref,
  ) => {
    const {
      refreshJiraPriorities,
      refreshAzurePriorities,
      updateJiraPriorities,
      updateAzurePriorities,
    } = useOrganizationService();

    const { manageJiraProjects, fetchJiraProjects, fetchJiraSettings } =
      useJiraService();

    const { manageAzureProjects, fetchAzureProjects, fetchAzureSettings } =
      useAzureService();

    const { syncIntegrationProject, fetchSprints } = useOrganizationService();

    const { orgDetails, jiraSettings, azureSettings } = useSelector(
      (state: any) => state.organization,
    );

    const [externalPrioritiesData, setExternalPrioritiesData] =
      useState<ExternalMapData>({});
    const [initialExternalMapping, setInitialExternalMapping] =
      useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showSyncAlert, setShowSyncAlert] = useState(false);
    const [showManageProjectsModal, setShowManageProjectsModal] =
      useState(false);
    const [isSyncingProjects, setIsSyncingProjects] = useState(false);
    const [projects, setProjects] = useState<any[]>([]);

    const internalPriorities = useMemo(
      () => orgDetails?.priorities ?? [],
      [orgDetails?.priorities],
    );

    const internalPriorityOptions = useMemo(() => {
      return internalPriorities.map((p: any, i: any) => ({
        value: p.id,
        label: `P${i + 1}`,
        color: p.color,
      }));
    }, [internalPriorities]);

    // Update local state when Redux settings change
    useEffect(() => {
      const settings =
        integrationType === "jira" ? jiraSettings : azureSettings;
      if (settings?.priorities) {
        setExternalPrioritiesData(settings.priorities);
        setInitialExternalMapping(JSON.stringify(settings.priorities));
      }
    }, [jiraSettings, azureSettings, integrationType]);

    const handleRefreshPriorities = async () => {
      try {
        setIsLoading(true);
        const res =
          integrationType === "jira"
            ? await refreshJiraPriorities()
            : await refreshAzurePriorities();

        if (res?.priorities) {
          setExternalPrioritiesData(res.priorities);
          setInitialExternalMapping(JSON.stringify(res.priorities));
          toast.success(`${externalLabel} priorities refreshed successfully`);
        }
      } catch (error) {
        console.error(`Error refreshing ${integrationType} priorities:`, error);
        toast.error(`Failed to refresh ${externalLabel} priorities`);
      } finally {
        setIsLoading(false);
      }
    };

    // Fetch integration projects to get IDs for resync
    useEffect(() => {
      const getProjects = async () => {
        try {
          const projectsList =
            integrationType === "jira"
              ? await fetchJiraProjects()
              : await fetchAzureProjects();
          setProjects(projectsList);
        } catch (error) {
          console.error("Error fetching projects:", error);
        }
      };
      if (showManageProjectsModal) {
        getProjects();
      }
    }, [
      integrationType,
      showManageProjectsModal,
    ]);

    const handleUpdatePriorities = async () => {
      try {
        setIsSaving(true);
        if (integrationType === "jira") {
          await updateJiraPriorities(externalPrioritiesData);
        } else {
          await updateAzurePriorities(externalPrioritiesData);
        }
        setInitialExternalMapping(JSON.stringify(externalPrioritiesData));
        setShowSyncAlert(true);
      } catch (error) {
        console.error(`Error updating ${integrationType} priorities:`, error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({
      update: handleUpdatePriorities,
    }));

    const hasChanges = useMemo(() => {
      return initialExternalMapping !== JSON.stringify(externalPrioritiesData);
    }, [externalPrioritiesData, initialExternalMapping]);

    const showPriorityMismatchAlert = useMemo(() => {
      const externalCount = Object.keys(externalPrioritiesData).length;
      return (
        externalCount > 0 &&
        internalPriorities.length < externalCount &&
        externalCount !== internalPriorities.length
      );
    }, [externalPrioritiesData, internalPriorities.length]);

    useEffect(() => {
      onHasChangesChange?.(hasChanges);
    }, [hasChanges, onHasChangesChange]);

    useEffect(() => {
      onIsSavingChange?.(isSaving);
    }, [isSaving, onIsSavingChange]);

    const handleMappingChangeLocal = (
      externalId: string,
      internalId: string,
    ) => {
      setExternalPrioritiesData((prev) => {
        const updatedData = { ...prev };

        // If internalId is not empty, clear it from other mappings
        if (internalId) {
          Object.keys(updatedData).forEach((key) => {
            if (
              key !== externalId &&
              updatedData[key].mapped_with === internalId
            ) {
              updatedData[key] = { ...updatedData[key], mapped_with: "" };
            }
          });
        }

        updatedData[externalId] = {
          ...updatedData[externalId],
          mapped_with: internalId,
        };

        return updatedData;
      });
    };

    useEffect(() => {
      onMappingChange?.(externalPrioritiesData);
    }, [externalPrioritiesData]);

    const externalLabel = integrationType === "azure" ? "Azure" : "Jira";

    if (isLoading) {
      return (
        <div className="flex flex-col gap-6 p-4">
          {/* Header Info Box Skeleton */}
          <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-40 animate-pulse"></div>
              <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-full animate-pulse"></div>
            </div>
            {showInternalUpdate && (
              <div className="w-24 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
            )}
          </div>

          <div className="flex flex-col gap-4 max-w-4xl mx-auto w-full">
            <div className="flex flex-col gap-3">
              {/* Table Header Skeleton */}
              <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700">
                <div className="col-span-5 flex justify-center">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16 animate-pulse"></div>
                </div>
                <div className="col-span-2"></div>
                <div className="col-span-5 flex justify-center">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse"></div>
                </div>
              </div>

              {/* Rows Skeleton */}
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-4 items-center bg-white dark:bg-gray-800/40 p-3 rounded-xl border border-gray-100 dark:border-gray-700"
                >
                  <div className="col-span-5 flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse flex-shrink-0"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20 animate-pulse"></div>
                  </div>
                  <div className="col-span-2 flex items-center justify-center">
                    <div className="h-[2px] w-8 bg-gray-100 dark:bg-gray-700"></div>
                  </div>
                  <div className="col-span-5">
                    <div className="h-10 bg-gray-50 dark:bg-gray-700/30 rounded-lg animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Note Box Skeleton */}
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/20 border border-gray-100 dark:border-gray-700 rounded-xl max-w-4xl mx-auto w-full flex justify-center">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 animate-pulse"></div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6 p-4 relative">
        {showSyncAlert ? (
          <div className="max-w-4xl mx-auto w-full mt-4">
            <CacheResetAlert
              title="Priority Mappings Updated"
              description="This change will reflect to all your projects during the next auto-sync, which may take up to 24 hours."
              buttonText="Sync Epics Now"
              onActionClick={() => setShowManageProjectsModal(true)}
              onClose={() => setShowSyncAlert(false)}
            />
          </div>
        ) : showPriorityMismatchAlert ? (
          <Alert color="info" icon={HiInformationCircle} className="mb-2">
            <span className="font-medium">Priority Mismatch!</span> The number
            of {externalLabel} priorities does not match your CadencePro
            priorities. You can manage or add more priorities in{" "}
            <Link
              to="/settings#OrgSettings"
              className="font-bold underline hover:text-blue-800 dark:hover:text-blue-600"
            >
              Organization Settings
            </Link>{" "}
            to ensure correct mapping.
          </Alert>
        ) : (
          <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-light-status-info-bg dark:bg-blue-900/30 p-4 rounded-lg border border-light-status-info-border dark:border-blue-600">
            <div className="flex-1 text-start">
              <h3 className="text-sm font-medium text-light-status-info-text dark:text-blue-300 mb-1">
                Integrate Priority Levels
              </h3>
              <p className="text-xs text-light-text-secondary dark:text-gray-400">
                Align your {externalLabel} priorities with CadencePro priority
                levels. This mapping will be used during import to correctly
                categorize work items.
              </p>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={() => {
                  handleRefreshPriorities();
                }}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-light hover:text-primary-hover dark:text-primary-dark dark:hover:text-primary-light border border-primary-light hover:border-primary-hover dark:border-primary-dark dark:hover:border-primary-light bg-secondary-light dark:bg-secondary-dark rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                    clipRule="evenodd"
                  />
                </svg>
                {isLoading ? "Refreshing" : "Refresh"}
              </button>
            </div>
            {showInternalUpdate && (
              <div className="flex-shrink-0">
                <button
                  onClick={handleUpdatePriorities}
                  disabled={!hasChanges || isSaving}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-white bg-primary-light hover:bg-primary-hover dark:bg-primary-dark dark:hover:bg-primary-light rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md transform active:scale-95"
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Updating...
                    </>
                  ) : (
                    "Update"
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-4 max-w-4xl mx-auto w-full">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700">
              <div className="col-span-5 text-xs font-bold text-gray-400 uppercase tracking-widest text-center">
                {externalLabel}
              </div>
              <div className="col-span-2"></div>
              <div className="col-span-5 text-xs font-bold text-gray-400 uppercase tracking-widest text-center">
                CADENCEPRO
              </div>
            </div>
            {Object.values(externalPrioritiesData).map((item, index) => {
              return (
                <div
                  key={item.id}
                  className="grid grid-cols-12 gap-4 items-center bg-white dark:bg-gray-800/40 p-3 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-sm transition-all"
                >
                  {/* External Priority Display */}
                  <div className="col-span-5 flex items-center justify-between gap-4 p-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full shadow-inner flex-shrink-0"
                        style={{
                          backgroundColor:
                            PRIORITY_COLORS[index] ??
                            PRIORITY_COLORS[PRIORITY_COLORS.length - 1],
                        }}
                      />
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                        {item.label}
                      </span>
                    </div>
                    <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-tighter">
                      {externalLabel}
                    </div>
                  </div>

                  {/* Connector */}
                  <div className="col-span-2 flex items-center justify-center">
                    <div className="flex items-center gap-1 text-gray-300 dark:text-gray-600">
                      <div className="h-[2px] w-8 bg-current"></div>
                      <HiOutlineChevronRight className="w-5 h-5 -ml-1" />
                    </div>
                  </div>

                  {/* Internal Selection */}
                  <div className="col-span-5">
                    <CustomDropdown
                      id={`mapping-${item.id}`}
                      name={`mapping-${item.id}`}
                      value={item.mapped_with}
                      options={internalPriorityOptions}
                      onChange={(val) => handleMappingChangeLocal(item.id, val)}
                      placeholder="Select CadencePro Priority"
                      className="!mb-0"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className=" mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl max-w-4xl mx-auto w-full">
          <p className="text-xs text-blue-700 dark:text-blue-300 text-center">
            Note: Select the corresponding <strong>CadencePro</strong> priority
            for each <strong>{externalLabel}</strong> priority level to ensure
            accurate data synchronization.
          </p>
        </div>

        <Portal>
          <ManageProjectsModal
            isOpen={showManageProjectsModal}
            onClose={() => setShowManageProjectsModal(false)}
            processedProjects={
              (integrationType === "jira" ? jiraSettings : azureSettings)
                ?.processed_projects || {}
            }
            onSave={async (changedProjects) => {
              try {
                setIsSyncingProjects(true);
                if (integrationType === "jira") {
                  await manageJiraProjects(changedProjects);
                } else {
                  await manageAzureProjects(changedProjects);
                }

                // Refresh settings
                if (integrationType === "jira") {
                  await fetchJiraSettings();
                } else {
                  await fetchAzureSettings();
                }
              } finally {
                setIsSyncingProjects(false);
              }
            }}
            onResync={async (projectName) => {
              try {
                setIsSyncingProjects(true);
                // Find the project ID from the projects array
                const project = projects.find((p) => p.label === projectName);
                const projectId = project?.value || projectName;

                await syncIntegrationProject(
                  projectName,
                  integrationType,
                  projectId,
                );
              } finally {
                setIsSyncingProjects(false);
              }
            }}
            integrationType={integrationType}
            isLoading={isSyncingProjects}
          />
        </Portal>
      </div>
    );
  },
);

export default PriorityMappingSelector;
